import React, { useState } from "react";
import {
  Typography,
  Chip,
  Stack,
  Accordion,
  AccordionDetails,
  Button,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CustomAccordionSummary from "styles/customAccordionSummary";
import {
  ALL,
  P_TYPE_PGTM,
  P_TYPE_RTM,
  P_TYPE_SRX,
  P_TYPE_UNKNOWN,
} from "constants/Constants";

export default function PolicyFilter({
  policyTypeOptions,
  selectedContract,
  selectedCab,
  setSelectedContract,
  setSelectedCab,
}) {
  const [resetSelected, setResetSelected] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const handleContractClick = (contract) => {
    setSelectedContract(contract);
    setResetSelected(false);
  };

  const handleCABClick = (cab) => {
    setSelectedCab(cab);
    setResetSelected(false);
  };

  const handleResetFilters = () => {
    setSelectedContract(ALL);
    setSelectedCab(ALL);
    setResetSelected(true);
  };

  const handleAccordionChange = () => {
    setExpanded(!expanded);
  };

  const isResetVisible = selectedContract !== ALL || selectedCab !== ALL;

  return (
    <div>
      <Accordion expanded={expanded} onChange={handleAccordionChange}>
        <CustomAccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel1a-content"
          id="panel1a-header"
        >
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0 }}>
            <Typography
              data-testid="policy-config-filter-title"
              variant="h6"
              sx={{ fontWeight: "bold" }}
            >
              Quick Filters:
            </Typography>
            {isResetVisible && (
              <Typography
                data-testid="policy-config-filter-reset-button"
                onClick={handleResetFilters}
                sx={{
                  cursor: "pointer",
                  color: resetSelected ? "selectedChip" : "chip",
                  fontWeight: resetSelected ? "bold" : "normal",
                  textDecoration: "underline",
                }}
              >
                Reset
              </Typography>
            )}
            {/* Display selected filters only when the accordion is collapsed */}
            {!expanded && selectedContract !== ALL && (
              <Typography variant="body2">
                <span
                  data-test-id="policy-config-filter-contract"
                  style={{ fontWeight: "bold" }}
                >
                  By Contract:
                </span>{" "}
                {selectedContract}
              </Typography>
            )}
            {!expanded && selectedCab !== ALL && (
              <Typography variant="body2">
                <span
                  data-test-id="policy-config-filter-status"
                  style={{ fontWeight: "bold" }}
                >
                  By Status:
                </span>{" "}
                {selectedCab}
              </Typography>
            )}
          </Stack>
        </CustomAccordionSummary>
        <AccordionDetails>
          <Stack
            direction="row"
            spacing={1}
            sx={{ mb: 1, flexWrap: "wrap", rowGap: 1 }}
          >
            <Typography
              data-test-id="policy-config-filter-policy-type-label"
              variant="subtitle1"
              sx={{ width: 125, fontWeight: "bold" }}
            >
              By Contract:
            </Typography>
            {/* Static ALL option */}
            <Chip
              data-test-id="policy-config-filter-policy-type"
              sx={{ minWidth: 125 }}
              key="ALL"
              label={ALL}
              onClick={() => handleContractClick(ALL)}
              color={selectedContract === ALL ? "selectedChip" : "chip"}
            />

            {/* Dynamic options */}
            {policyTypeOptions.map(({ policyTypeKey, policyType }) => (
              <Chip
                data-test-id="policy-config-filter-policy-type"
                sx={{ minWidth: 125 }}
                key={policyTypeKey}
                label={policyType}
                onClick={() => handleContractClick(policyType)}
                color={
                  selectedContract === policyType ? "selectedChip" : "chip"
                }
              />
            ))}

            {/* Static UNKNOWN fallback */}
            <Chip
              data-test-id="policy-config-filter-policy-type"
              sx={{ minWidth: 125 }}
              key="UNKNOWN"
              label={P_TYPE_UNKNOWN}
              onClick={() => handleContractClick(P_TYPE_UNKNOWN)}
              color={
                selectedContract === P_TYPE_UNKNOWN ? "selectedChip" : "chip"
              }
            />
          </Stack>
          <Stack
            direction="row"
            spacing={1}
            sx={{ mb: 1, flexWrap: "wrap", rowGap: 1 }}
          >
            <Typography
              data-test-id="policy-config-filter-last-cab-label"
              variant="subtitle1"
              sx={{ width: 125, fontWeight: "bold" }}
            >
              By Last CAB:
            </Typography>
            {[ALL, "Q1", "Q2", "Q3", "Q4"].map((cab) => (
              <Chip
                data-test-id="policy-config-filter-last-cab"
                sx={{ minWidth: 125 }}
                key={cab}
                label={cab}
                onClick={() => handleCABClick(cab)}
                color={selectedCab === cab ? "selectedChip" : "chip"}
              />
            ))}
          </Stack>
        </AccordionDetails>
      </Accordion>
    </div>
  );
}
